/**
 * Bridges SPA AttentionItem / AgentItem view-models onto the shared
 * ResourceRelation graph so Radar and inspector can cluster multi-source work
 * without depending on a single ticket string.
 */

import {
  canonicalGitHubResourceId,
  canonicalJiraResourceId,
  canonicalManualHubResourceId,
  clusterResourcesByRelations,
  parseGitHubPullRequestId,
  provisionalCardResourceId,
  relationToJiraIssue,
  upsertResourceRelation,
  type ManualWorkHub,
  type ResourceId,
  type ResourceRelation,
  type WorkContext,
} from '@zunelo/shared';
import type { AgentItem, AttentionItem, NoteItem, ReminderItem } from './types';

const OBSERVED_AT_FALLBACK = '1970-01-01T00:00:00.000Z';

function observedAtForItem(item: AttentionItem): string {
  return item.updatedAt || OBSERVED_AT_FALLBACK;
}

function relationTypeForSource(source: AttentionItem['source']): 'implements' | 'discusses' | 'mentions' | 'related' {
  if (source === 'github') return 'implements';
  if (source === 'slack') return 'discusses';
  if (source === 'gmail') return 'mentions';
  return 'related';
}

/** Stable resource id for an attention card (canonical when possible). */
export function resourceIdForAttentionItem(item: AttentionItem): ResourceId {
  if (item.source === 'jira') {
    const key = (item.ticket?.trim() || item.primary?.trim() || item.id.trim());
    if (!key) {
      return provisionalCardResourceId('jira', item.id || 'unknown');
    }
    return canonicalJiraResourceId(key);
  }
  if (item.source === 'github') {
    const parsed =
      parseGitHubPullRequestId(item.id) ||
      parseGitHubPullRequestId(item.primary) ||
      parseGitHubPullRequestId(item.primary?.replace(/^#/, 'PR-') ?? '');
    // Only use canonical github:owner/repo#N when owner+repo are known.
    // Number-only ids would collide across repos (`github:_/provisional#412`).
    if (parsed?.owner && parsed.repo && parsed.number > 0) {
      return canonicalGitHubResourceId(parsed.owner, parsed.repo, parsed.number);
    }
  }
  return provisionalCardResourceId(item.source, item.id);
}

export function resourceIdForNote(note: Pick<NoteItem, 'id'>): ResourceId {
  return provisionalCardResourceId('notes', note.id);
}

export function resourceIdForReminder(reminder: Pick<ReminderItem, 'id'>): ResourceId {
  return provisionalCardResourceId('reminders', reminder.id);
}

type LinkBuckets = { confirmed: string[]; suggested: string[] };

/**
 * Primary hub links become confirmed WorkContext edges.
 * Extra refs (e.g. other keys mentioned in a Slack thread) stay suggested so
 * they surface in the inspector without merging unrelated Jira hubs.
 */
function linkBucketsForItem(item: AttentionItem): LinkBuckets {
  const ticket = item.ticket?.trim();
  const refKeys = (item.refs ?? [])
    .map((ref) => ref.key?.trim())
    .filter((key): key is string => Boolean(key));
  if (ticket) {
    return {
      confirmed: [ticket],
      suggested: refKeys.filter((key) => key !== ticket),
    };
  }
  if (refKeys.length === 0) return { confirmed: [], suggested: [] };
  return { confirmed: [refKeys[0]!], suggested: refKeys.slice(1) };
}

function appendTicketRelations(
  relations: ResourceRelation[],
  fromResourceId: ResourceId,
  item: AttentionItem,
): ResourceRelation[] {
  let next = relations;
  const observedAt = observedAtForItem(item);
  const type = relationTypeForSource(item.source);
  const { confirmed, suggested } = linkBucketsForItem(item);
  for (const key of confirmed) {
    if (item.source === 'jira' && canonicalJiraResourceId(key) === fromResourceId) continue;
    next = upsertResourceRelation(
      next,
      relationToJiraIssue(fromResourceId, key, type, observedAt, `${item.id}:confirmed:${key}`, 'confirmed'),
    );
  }
  for (const key of suggested) {
    if (item.source === 'jira' && canonicalJiraResourceId(key) === fromResourceId) continue;
    next = upsertResourceRelation(
      next,
      relationToJiraIssue(fromResourceId, key, 'mentions', observedAt, `${item.id}:suggested:${key}`, 'suggested'),
    );
  }
  return next;
}

/** Derive confirmed ResourceRelations from loaded attention cards (client-side). */
export function deriveRelationsFromAttentionItems(
  items: readonly AttentionItem[],
): ResourceRelation[] {
  let relations: ResourceRelation[] = [];
  for (const item of items) {
    relations = appendTicketRelations(relations, resourceIdForAttentionItem(item), item);
  }
  return relations;
}

function agentResourceId(agent: AgentItem): ResourceId {
  return provisionalCardResourceId('agents', agent.name);
}

function appendAgentRelations(
  relations: ResourceRelation[],
  agents: readonly AgentItem[],
): ResourceRelation[] {
  let next = relations;
  for (const agent of agents) {
    const ticket = agent.ticket?.trim();
    if (!ticket) continue;
    next = upsertResourceRelation(
      next,
      relationToJiraIssue(
        agentResourceId(agent),
        ticket,
        'related',
        OBSERVED_AT_FALLBACK,
        `agent:${agent.name}:${ticket}`,
      ),
    );
  }
  return next;
}

export interface AttentionWorkContextCluster {
  context: WorkContext;
  items: AttentionItem[];
  agents: AgentItem[];
  notes: NoteItem[];
  reminders: ReminderItem[];
  /** Display key: Jira issue key when present, else manual hub / primary/id fallback. */
  displayKey: string;
  /** Manual hub title when the cluster is anchored on a user-created context. */
  manualTitle?: string;
  isManual: boolean;
}

export interface ClusterAttentionOptions {
  extraRelations?: readonly ResourceRelation[];
  manualHubs?: readonly ManualWorkHub[];
  notes?: readonly NoteItem[];
  reminders?: readonly ReminderItem[];
}

function displayKeyForCluster(
  context: WorkContext,
  items: AttentionItem[],
  agents: AgentItem[],
  manualHubs: readonly ManualWorkHub[],
): { displayKey: string; manualTitle?: string; isManual: boolean } {
  const jira = items.find((item) => item.source === 'jira');
  if (jira?.ticket) return { displayKey: jira.ticket, isManual: false };
  if (jira?.primary) return { displayKey: jira.primary, isManual: false };
  const ticketed = items.find((item) => item.ticket?.trim());
  if (ticketed?.ticket) return { displayKey: ticketed.ticket, isManual: false };
  const refKey = items.flatMap((item) => item.refs ?? []).map((ref) => ref.key)[0];
  if (refKey) return { displayKey: refKey, isManual: false };
  if (agents[0]?.ticket) return { displayKey: agents[0].ticket, isManual: false };

  const anchor = String(context.anchorResourceId);
  if (anchor.startsWith('manual:')) {
    const hubId = anchor.slice('manual:'.length);
    const hub = manualHubs.find((h) => h.id === hubId);
    return {
      displayKey: hubId,
      manualTitle: hub?.title,
      isManual: true,
    };
  }

  const primary = items[0]?.primary || items[0]?.id;
  if (primary) return { displayKey: primary, isManual: false };
  return { displayKey: anchor, isManual: false };
}

function collectMemberIdsFromRelations(relations: readonly ResourceRelation[]): ResourceId[] {
  const ids: ResourceId[] = [];
  for (const relation of relations) {
    if (relation.status !== 'confirmed') continue;
    ids.push(relation.fromResourceId, relation.toResourceId);
  }
  return ids;
}

function isRelationList(
  value: ClusterAttentionOptions | readonly ResourceRelation[],
): value is readonly ResourceRelation[] {
  return Array.isArray(value);
}

/**
 * Cluster attention items + agents into WorkContexts using derived relations
 * plus any server-provided graph edges / manual hubs / tool members.
 */
export function clusterAttentionIntoWorkContexts(
  items: readonly AttentionItem[],
  agents: readonly AgentItem[] = [],
  options: ClusterAttentionOptions | readonly ResourceRelation[] = {},
): AttentionWorkContextCluster[] {
  const opts: ClusterAttentionOptions = isRelationList(options)
    ? { extraRelations: options }
    : options;
  const extraRelations = opts.extraRelations ?? [];
  const manualHubs = opts.manualHubs ?? [];
  const notes = opts.notes ?? [];
  const reminders = opts.reminders ?? [];

  let relations = deriveRelationsFromAttentionItems(items);
  relations = appendAgentRelations(relations, agents);
  for (const edge of extraRelations) {
    relations = upsertResourceRelation(relations, edge);
  }

  const itemByResource = new Map<string, AttentionItem>();
  const seeds: ResourceId[] = [];
  for (const item of items) {
    const id = resourceIdForAttentionItem(item);
    itemByResource.set(String(id), item);
    seeds.push(id);
    for (const key of linkBucketsForItem(item).confirmed) {
      seeds.push(canonicalJiraResourceId(key));
    }
  }

  const agentByResource = new Map<string, AgentItem>();
  for (const agent of agents) {
    if (!agent.ticket?.trim()) continue;
    const id = agentResourceId(agent);
    agentByResource.set(String(id), agent);
    seeds.push(id);
    seeds.push(canonicalJiraResourceId(agent.ticket.trim()));
  }

  const noteByResource = new Map<string, NoteItem>();
  for (const note of notes) {
    const id = resourceIdForNote(note);
    noteByResource.set(String(id), note);
  }
  const reminderByResource = new Map<string, ReminderItem>();
  for (const reminder of reminders) {
    const id = resourceIdForReminder(reminder);
    reminderByResource.set(String(id), reminder);
  }

  for (const hub of manualHubs) {
    seeds.push(canonicalManualHubResourceId(hub.id));
  }
  for (const id of collectMemberIdsFromRelations(extraRelations)) {
    seeds.push(id);
  }

  const contexts = clusterResourcesByRelations(seeds, relations);
  const clusters: AttentionWorkContextCluster[] = [];
  for (const context of contexts) {
    const clusteredItems: AttentionItem[] = [];
    const clusteredAgents: AgentItem[] = [];
    const clusteredNotes: NoteItem[] = [];
    const clusteredReminders: ReminderItem[] = [];
    for (const memberId of context.memberResourceIds) {
      const key = String(memberId);
      const item = itemByResource.get(key);
      if (item) clusteredItems.push(item);
      const agent = agentByResource.get(key);
      if (agent) clusteredAgents.push(agent);
      const note = noteByResource.get(key);
      if (note) clusteredNotes.push(note);
      const reminder = reminderByResource.get(key);
      if (reminder) clusteredReminders.push(reminder);
    }
    const meta = displayKeyForCluster(context, clusteredItems, clusteredAgents, manualHubs);
    const hasMembers =
      clusteredItems.length > 0 ||
      clusteredAgents.length > 0 ||
      clusteredNotes.length > 0 ||
      clusteredReminders.length > 0 ||
      meta.isManual;
    if (!hasMembers) continue;
    clusters.push({
      context,
      items: clusteredItems,
      agents: clusteredAgents,
      notes: clusteredNotes,
      reminders: clusteredReminders,
      displayKey: meta.displayKey,
      manualTitle: meta.manualTitle,
      isManual: meta.isManual,
    });
  }
  return clusters;
}
